import { Interval, IntervalUnit, SavedSearch } from '@types';
import { useEffect, useMemo } from 'react';
import {
  findOptimalIntervalUnit,
  getValidIntervalOptions,
  validateTimeRange,
} from './intervalCalculations';

interface UseIntervalValidationConfig {
  interval: Interval;
  activeSearch: SavedSearch;
  setSearchField: <T>(field: string, value: T) => void;
  pointsLimit: number;
}

interface UseIntervalValidationResult {
  validOptions: IntervalUnit[];
  isValidTimeRange: boolean;
}

export const useIntervalValidation = ({
  interval,
  activeSearch,
  setSearchField,
  pointsLimit,
}: UseIntervalValidationConfig): UseIntervalValidationResult => {
  const dataPointsCount = activeSearch.dataPoints?.length || 0;

  const timeRangeInfo = useMemo(
    () =>
      validateTimeRange(activeSearch.startDateTime, activeSearch.stopDateTime),
    [activeSearch.startDateTime, activeSearch.stopDateTime],
  );

  const validOptions = useMemo(
    () =>
      getValidIntervalOptions(
        timeRangeInfo.rangeMs,
        interval.value,
        dataPointsCount,
        pointsLimit,
      ),
    [timeRangeInfo.rangeMs, interval.value, dataPointsCount, pointsLimit],
  );

  useEffect(() => {
    if (!timeRangeInfo.isValid || !interval.unit || !interval.value) {
      return;
    }

    const optimalUnit = findOptimalIntervalUnit(
      timeRangeInfo.rangeMs,
      interval.value,
      dataPointsCount,
      pointsLimit,
      interval.unit,
    );

    if (optimalUnit && optimalUnit !== interval.unit) {
      setSearchField<Interval>('interval', {
        ...interval,
        unit: optimalUnit,
      });
    }
  }, [timeRangeInfo, interval, dataPointsCount, pointsLimit, setSearchField]);

  return {
    validOptions,
    isValidTimeRange: timeRangeInfo.isValid,
  };
};
